import { PermissionsDto, UserDetailsDto } from "@/client";

// Type definitions for better type safety
type PermissionValue = boolean | Record<string, unknown>;
type PermissionObject = Record<string, PermissionValue>;
type PermissionsStructure = Record<string, PermissionObject>;

// Helper function to safely access nested data using a dot-path
export const getNestedData = (
  obj: Record<string, unknown>,
  path: string | null,
): unknown => {
  if (!path) return null;
  const keys = path.split(".");
  let result: unknown = obj;
  for (const key of keys) {
    if (result && typeof result === "object" && result !== null) {
      result = (result as Record<string, unknown>)[key];
    } else {
      return null;
    }
    if (result === undefined) return null;
  }
  return result;
};

// Evaluates a JSONLogic rule against the full permission set for a given role
export const evaluateRule = (
  rule: Record<string, unknown>,
  allPermissions: Record<string, unknown>,
  role: string,
): boolean => {
  // Handle the 'or' operator
  if (rule.or && Array.isArray(rule.or)) {
    for (const condition of rule.or) {
      // Handle the 'var' operator to look up another permission's value
      if (condition && typeof condition === "object" && "var" in condition) {
        const permissionValue = getNestedData(
          allPermissions,
          `${condition.var}.${role}`,
        );
        if (permissionValue === true) {
          return true; // If any condition in an 'or' block is true, the result is true.
        }
      }
    }
  }
  return false;
};

// Helper function to extract permissions for a specific role
export const extractPermissionsForRole = (
  allPermissions: Record<string, unknown>,
  role: UserDetailsDto["role"],
): PermissionsDto => {
  const extractedPermissions: Record<string, unknown> = {};

  const extractRecursively = (
    obj: Record<string, unknown>,
    target: Record<string, unknown>,
  ) => {
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === "object" && value !== null) {
        const valueObj = value as Record<string, unknown>;

        // Check if this object has role keys (boolean values for roles)
        if (role in valueObj && typeof valueObj[role] === "boolean") {
          // This is a permission object with role keys
          if (valueObj.rule && typeof valueObj.rule === "object") {
            // If there's a rule, evaluate it instead of using the static boolean value
            target[key] = evaluateRule(
              valueObj.rule as Record<string, unknown>,
              allPermissions,
              role,
            );
          } else {
            // No rule, use the static boolean value
            target[key] = valueObj[role];
          }
        } else if (key !== "description" && key !== "rule") {
          // This is a nested object, recurse into it
          target[key] = {};
          extractRecursively(valueObj, target[key] as Record<string, unknown>);
        }
      }
    }
  };

  extractRecursively(allPermissions, extractedPermissions);
  return extractedPermissions as PermissionsDto;
};
